<script lang="tsx">
  import { computed, defineComponent, getCurrentInstance, type PropType } from "vue";

  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faAngleDown, faAngleUp, faSearch, faLanguage, faUser, faBars } from "@fortawesome/free-solid-svg-icons";

  import {
    vIntersectionObserver as intersectionObserver, vElementBounding as elementBounding,
    vElementSize as elementSize,
  } from "@vueuse/components";

  import { useCssVar, type ElementSize, type UseElementBoundingReturn } from "@vueuse/core";
  // this type is not exported :(
  type ElementBounding = Omit<UseElementBoundingReturn, "update">;


  import { Button, FloatLabel, InputGroup, InputText, Menu, Popover, Ripple, type MenuMethods, type PopoverMethods } from "primevue";
  import type { MenuItem } from "primevue/menuitem";

  import NavPopover from "./NavPopover.vue";

  import { useClientHandle } from "@urql/vue";
  import { graphql as gql } from "@/gql";
  import type { global_Entry, image_Entry, menuNode_Entry, MenuTreeType, Scalars } from "@/gql/graphql";


  interface EntrySites {
    [key: string]: {
      lang: string,
      language: string,
      title: string | null,
      url: string
    }
  };

  const Navbar = defineComponent({
    components: {
      Menu, Button, Popover,
      NavPopover, Icon
    },
    directives: { intersectionObserver, elementBounding, elementSize, ripple: Ripple },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      },
      entry: {
        type: [Number, String] as PropType<Scalars["QueryArgument"]["input"]>,
        required: false,
      },
    },
    async setup(props) {
      const proxy = getCurrentInstance()!.proxy as InstanceType<typeof Navbar>;
      const handle = useClientHandle();

      const { data: globalData } = await handle.useQuery({
        query: gql(`
          query FetchNavbarInfo($site: String, $user: Int) {
            globalEntry(site: [$site]) {
              ... on global_Entry {
                id,
                logo {
                  url,
                  alt,
                  width,
                  height,
                },
                header {
                  url,
                  alt,
                  width,
                  height,
                },
                nameSite,
                nameLong,
                menuMain {
                  ... on menuNode_Entry {
                    tree(user: $user) {
                      label,
                      href,
                      children {
                        label,
                        href,
                        children {
                          label,
                          href,
                          children {
                            label,
                            href,
                            children {
                              label,
                              href,
                              children {
                                label,
                                href,
                                children {
                                  label,
                                  href,
                                  children {
                                    label,
                                    href,
                                    children {
                                      label,
                                      href,
                                      children {
                                        label,
                                        href,
                                        children {
                                          label,
                                          href,
                                        }
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }

            homeEntry(site: [$site]) { ... on EntryInterface { url } }
            encaHomeEntry: homeEntry(site: "enca") { ... on EntryInterface { url } }
            frcaHomeEntry: homeEntry(site: "frca") { ... on EntryInterface { url } }
          }
        `),
        variables: { site: props.site, user: proxy.$session.info.value?.id }
      });

      // This query will pretty much never be available in cache, and it's only
      // used in a submenu so don't wait on it...
      const entryQuery = handle.useQuery({
        query: gql(`
          query FetchNavbarEntryTranslations($entry: QueryArgument) {
            encaEntry: entry(id: [$entry], site: "enca") { title, url }
            frcaEntry: entry(id: [$entry], site: "frca") { title, url }
          }
        `),
        variables: { entry: props.entry ?? null },
        pause: props.entry == null,
      });

      return {
        tree: computed((): MenuTreeType[] => {
          const tree = [];
          for (const rootNode of globalData.value!.globalEntry!.menuMain) {
            if (rootNode?.tree != null) {
              tree.push(rootNode!.tree!);
            }
          }
          return tree;
        }),
        global: computed((): global_Entry => {
          return globalData.value!.globalEntry! as global_Entry;
        }),
        homeUrl: computed((): string => {
          return globalData.value!.homeEntry!.url!;
        }),
        entrySites: computed((): EntrySites | undefined => {
          return {
            "enca": {
              lang: "en-ca",
              language: "English",
              title: entryQuery.data.value?.encaEntry?.title ?? null,
              url: entryQuery.data.value?.encaEntry?.url ?? globalData.value!.encaHomeEntry!.url!,
            },
            "frca": {
              lang: "fr-ca",
              language: "Français",
              title: entryQuery.data.value?.frcaEntry?.title ?? null,
              url: entryQuery.data.value?.frcaEntry?.url ?? globalData.value!.frcaHomeEntry!.url!,
            },
          }
        }),
        css: {
          // height of the first grid row; the available height for our logo
          firstRowHeight: useCssVar("--navbar-first-row-height"),
          // height of the first two grid rows; the available height for our logo
          // the aspect ratio of the logo image
          logoAspectRatio: useCssVar("--navbar-logo-aspect-ratio"),
        }
      };
    },

    data() { return {
      bounds: undefined as ElementBounding | undefined,
      popoverRefs: new Map() as Map<number, InstanceType<typeof Popover> | null>,
      selected: null as (number | null),
      shortSelected: false,

      faAngleDown, faAngleUp, faSearch, faLanguage, faUser, faBars
    }},

    computed: {
      shortTree(): MenuTreeType {
        return {
          "__typename": "MenuTreeType",
          "label": "Menu",
          "children": this.tree,
        }
      },
      siteMenuItems(): MenuItem[] {
        const items: MenuItem[] = [];
        for (const key in this.entrySites) {
          const value = this.entrySites[key];
          items.push({
            item: value,
            label: value.title !== null
              ? `${value.language} ${value.title}`
              : `${value.language}`,
            url: value.url,
          });
        }
        return items;
      },
      userMenuItems(): MenuItem[] {
        const session = this.$session.info;

        const adminHostname = location.hostname === "childcaretoday.ca" ? "adco-o.on.ca" : location.hostname;
        const adminOrigin = `https://${adminHostname}${location.port}`;

        const adminItems: { label: string; url: string; }[] = [];
        if (session.value?.groups?.includes("staff") || session.value?.admin === true) {
          adminItems.push({
            label: {
              "enca": "Craft Control Panel",
              "frca": "Panneau de commande Craft",
            }[this.site],
            url: {
              "enca": `${adminOrigin}/admin?site=enca`,
              "frca": `${adminOrigin}/admin?site=frca`,
            }[this.site],
          });

          if (this.entry !== undefined) {
            adminItems.push({
              label: {
                "enca": "Edit this Entry",
                "frca": "Modifier cette entrée",
              }[this.site],
              url: {
                "enca": `/admin/content/entries/entry/${this.entry}?site=enca`,
                "frca": `/admin/content/entries/entry/${this.entry}?site=frca`,
              }[this.site],
            });
          }
        }

        let items: { label: string; url: string; }[]
        if (session.value?.isGuest === false) {
          items = [
            {
              label: {
                "enca": "Edit Account",
                "frca": "Modifier mon compte",
              }[this.site],
              url: {
                "enca": "/en-ca/account/edit",
                "frca": "/fr-ca/compte/modifier",
              }[this.site],
            },
            ...adminItems,
            {
              label: {
                "enca": "Logout",
                "frca": "Déconnexion",
              }[this.site],
              url: {
                "enca": "/en-ca/account/logout",
                "frca": "/fr-ca/compte/déconnexion",
              }[this.site],
            }
          ];
        } else {
          items = [
            {
              label: {
                "enca": "Login",
                "frca": "Connexion",
              }[this.site],
              url: {
                "enca": "/en-ca/account/login",
                "frca": "/fr-ca/compte/connexion",
              }[this.site],
            },
            {
              label: {
                "enca": "Forgot Password",
                "frca": "Mot de passe oublié",
              }[this.site],
              url: {
                "enca": "/en-ca/account/reset-password",
                "frca": "/fr-ca/compte/réinitialiser-mot-de-passe",
              }[this.site],
            },
            {
              label: {
                "enca": "Become a Member",
                "frca": "Devenez membre",
              }[this.site],
              url: {
                "enca": "/en-ca/membership",
                "frca": "/fr-ca/membership",
              }[this.site],
            }
          ];
        }

        return items;
      },
      logoAspectRatio(): number {
        const logo = this.global?.logo[0];
        if (logo?.height == null || logo?.width == null) { return 1; }
        return logo.width / logo.height;
      }
    },

    watch: {
      logoAspectRatio: {
        handler(value: number) {
          this.css.logoAspectRatio.value = value.toString();
        },
        immediate: true,
      }
    },

    methods: {
      select(i: number, event: Event) {
        if (this.$data.selected !== null) {
          this.$data.popoverRefs.get(this.$data.selected)!.hide();
        }

        this.$data.selected = i;
        this.$data.popoverRefs.get(i)!.show(event);
      },
      unselect(i: number | null = null) {
        if (i != null) {
          this.$data.popoverRefs.get(i)!.hide();
          if (this.$data.selected == i) {
            this.$data.selected = null;
          }
        } else if (this.$data.selected !== null) {
          this.$data.popoverRefs.get(this.$data.selected)!.hide();
          this.$data.selected = null;
        }
      },
      openShortMenu(event: Event) {
        const popover = (this.$refs.shortPopover as InstanceType<typeof Popover>);
        popover.show(event);
        this.shortSelected = true;
      },
      closeShortMenu() {
        const popover = (this.$refs.shortPopover as InstanceType<typeof Popover>);
        popover.hide();
        this.shortSelected = false;
      },
      onBounding(bounds: ElementBounding) {
        this.$data.bounds = bounds;
      },
    },
  });

  export default Navbar;
</script>

<template>
  <nav
    v-element-bounding="[
      onBounding,
      {immediate: true, windowScroll: false, windowResize: true, updateTiming: 'next-frame'}
    ]">
    <a class="logo" :href="homeUrl">
      <img :src="global.logo[0]!.url ?? undefined"
        :title="global.logo[0]!.title ?? undefined" :alt="global.logo[0]!.alt ?? undefined"
        :width="global.logo[0]!.width ?? undefined" :height="global.logo[0]!.height ?? undefined" />
    </a>

    <a class="title" :href="homeUrl">
      <img :src="global.header[0]!.url ?? undefined"
        :title="global.header[0]!.title ?? undefined" :alt="global.header[0]!.alt ?? undefined"
        :width="global.header[0]!.width ?? undefined" :height="global.header[0]!.height ?? undefined" />
    </a>
    <div class="title-measure" v-element-size="({height}: ElementSize) => css.firstRowHeight.value = height + 'px'"></div>


    <ul class="linktree">
      <li class="short">
        <Button
          size="normal" raised
          @click.prevent="shortSelected ? closeShortMenu() : openShortMenu($event)"
          label="Menu" icon-pos="right">
          <template #icon="slot">
            <Icon :class="['fa', slot.class]" :icon="shortSelected ? faAngleUp : faBars"/>
          </template>
        </Button>

        <Popover class="popover" dismissable append-to="self"
        ref="shortPopover" @hide="closeShortMenu">
          <NavPopover :tree="shortTree"
            @close="closeShortMenu"/>
        </Popover>
      </li>

      <li class="long" v-for="(child, i) in tree" :class="selected === i ? 'selected' : ''">

        <Button v-if="!(child.children != null && child.children.length > 0)
          && child.href == null"
          class="text" size="normal" variant="link" severity="secondary"
          :label="child.label!" disabled/>

        <Button v-if="!(child.children != null && child.children.length > 0)
          && child.href != null"
          class="text" size="normal" variant="link" severity="secondary"
          :label="child.label!" as="a" :href="child.href" style="text-decoration: none"/>

        <!-- <Button v-if="child.children != null && child.children.length > 0
          && child.href == null" -->
        <Button v-if="child.children != null && child.children.length > 0"
          class="text" size="normal" variant="link" severity="secondary"
          @click.prevent="selected !== i ? select(i, $event) : unselect()"
          :label="child.label!" icon-pos="right">
          <template #icon="slot" v-if="child.children != null && child.children.length > 0">
            <Icon :class="['fa', slot.class]" :icon="selected === i ? faAngleUp : faAngleDown"/>
          </template>
        </Button>

        <!-- <InputGroup>
          <Button v-if="child.children != null && child.children.length > 0
            && child.href != null"
            class="text" size="normal" variant="link" severity="secondary"
            :label="child.label!" as="a" :href="child.href" style="text-decoration: none"/>
          <Button v-if="child.children != null && child.children.length > 0
            && child.href != null"
            class="text" size="normal" variant="link" severity="secondary"
            @click.prevent="selected !== i ? select(i, $event) : unselect()">
            <template #icon>
              <Icon v-if="child.children != null && child.children.length > 0"
                class="fa" :icon="selected === i ? faAngleUp : faAngleDown"/>
            </template>
          </Button>
        </InputGroup> -->

        <Popover class="popover" dismissable append-to="self" @hide="unselect(i)"
          :ref="ref => popoverRefs.set(i, ref as any)">
          <NavPopover :tree="child" @close="unselect"/>
        </Popover>
      </li>

        <!-- <li class="overflow">
          <Button v-if="child.children != null && child.children.length > 0"
            class="text" size="normal" variant="link" severity="secondary"
            @click.prevent="selected !== i ? select(i, $event) : unselect()"
            style="gap: calc(var(--p-button-padding-x) * 2)">
            <div class='button-group'>
              <span class="p-button-label" data-pc-section="label">{{ child.label! }}</span>
              <Icon v-if="child.children != null && child.children.length > 0"
                class="fa" :icon="selected === i ? faAngleUp : faAngleDown"/>
            </div>
          </Button>

          <Popover class="overflow-popover" dismissable append-to="self" @hide="unselect(0)"
            :ref="ref => popoverRefs.set(0, ref as any)">
            <NavPopover :tree="overflow" @close="unselect"/>
          </Popover>
        </li> -->

      <li class="rest">
        <!-- <Button aria-label="Language"
          size="normal" raised
          @click.prevent="($refs.siteMenu as MenuMethods).toggle($event)">
          <template #icon>
            <Icon class="fa" :icon="faLanguage"/>
          </template>
        </Button> -->
        <Menu ref="siteMenu" :model="siteMenuItems" :popup="true">
          <template #item="{ item, props }">
            <a class="languageItem" v-ripple v-bind="props.action" :href="item.url" :lang="item.item.lang">
              <span>{{ item.item.language }}</span>
              <small v-if="item.item.title != null">{{ item.item.title }}</small>
            </a>
          </template>
        </Menu>
      </li>

      <li class="rest">
        <Button aria-label="User"
          size="normal" raised
          @click.prevent="($refs.userMenu as PopoverMethods).toggle($event)">
          <template #icon>
            <Icon class="fa" :icon="faUser"/>
          </template>
        </Button>
        <Menu ref="userMenu" :model="userMenuItems" :popup="true"/>
        <!-- <Popover ref="userMenu" append-to="self">
          <LoginForm v-if="$session.info.value?.isGuest === true"/>
          <Menu v-if="$session.info.value?.isGuest === false"/>
        </Popover> -->
      </li>
    </ul>
  </nav>
</template>

<style lang="less" scoped>
  @import (reference) "@/style/main.less";

  :global(:root) {
    // set from script: --navbar-first-row-height,
    // --navbar--logo-aspect-ratio.

    --navbar-row-height: calc((var(--p-button-padding-y) + 0.5em) * 2 + 1em * var(--line-height));
  }

  nav {
    background-color: var(--p-primary-50);

    // border-block-end: 1px solid var(--color-border);
    // border-inline: 1px solid var(--color-border);
    border-radius: 0 0 var(--spacing-radius) var(--spacing-radius);
    box-shadow:
      0 var(--spacing-shadow) var(--spacing-shadow) 0 var(--color-shadow),
      calc(var(--spacing-shadow) * -1) 0 var(--spacing-shadow) 0 var(--color-shadow),
      var(--spacing-shadow) 0 var(--spacing-shadow) 0 var(--color-shadow);

    height: 100%;
    width: 100%;

    display: grid;
    grid:
      "logo title title" max-content
      "linktree linktree linktree" var(--navbar-row-height)
      "linktree linktree linktree" max-content
      / fit-content(10em) 1fr 10em;
    align-items: center;
    justify-content: center;

    @media (width < @screen-width-small) {
      grid:
        "title title title" max-content
        "linktree linktree linktree" var(--navbar-row-height)
        "linktree linktree linktree" max-content
        / fit-content(10em) 1fr 10em;

      & > .logo { display: none; }
    }

    & > .logo { grid-area: logo; }
    & > .logo-measure { grid-area: logo; }
    & > .logo > img { grid-area: logo; }
    & > .title { grid-area: title; }
    & > .title-measure { grid-area: title; }
    & > .search { grid-area: search; }
    & > .linktree { grid-area: linktree; }

    // ensure that the border radius is clipped on the image
    & > .logo {
      &, & * {
        border-end-start-radius: var(--spacing-radius);
      }
    }

    & > .linktree {
      padding-block: 0.5em;
      background-color: var(--p-secondary-800);
      border-radius: 0 0 var(--spacing-radius) var(--spacing-radius);
    }
  }

  .logo {
    display: grid;
    grid: subgrid / subgrid;
    z-index: 1;

    img {
      align-self: end;

      height: 8em;
      max-width: 100%;
      width: auto;

      padding: calc(var(--spacing-inline) * 0.5);
    }
  }

  .logo-measure, .title-measure {
    position: relative;
    align-self: stretch;
    pointer-events: none;
  }

  .title {
    margin-block-start: var(--spacing-inline);
    margin-inline: var(--spacing-block);
    img {
      max-width: 100%;
      height: auto;
    }

    justify-self: flex-start;
  }

  .linktree {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: start;
    justify-content: center;
    padding-inline: var(--spacing-inline);
    gap: var(--spacing-inline);

    & > * { flex: 0 0 auto; }

    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -webkit-user-select: none;

    // filler to start-align last row
    &::after {
      content: "";
      flex: 1 0 auto;
      min-width: 0;
    }

    & .text {
      --p-button-padding-x: 0.25em;
      --p-button-link-color: var(--p-neutral-50);
      --p-button-link-hover-color: var(--p-neutral-50);
      --p-button-link-active-color: var(--p-neutral-300);
    }

    & > .long {
      @media screen and (width < @screen-width-small) {
        display: none;
      }
    }

    & > .short {
      @media screen and (width >= @screen-width-small) {
        display: none;
      }
    }

    // & > .rest {
    //   display: inline-flex;
    //   gap: var(--spacing-inline);
    // }
  }

  :deep(.fa path) {
    transition: 0.25s;
  }

  .languageItem {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  .button-group {
    display: inline-flex;
    gap: 0;
    align-items: center;
    justify-items: center;
    gap: calc(var(--spacing-inline) * 0.5);
  }
</style>
